<script setup lang="ts">
import { computed } from 'vue';
import UiIconButton from './UiIconButton.vue';

type Tone = 'info' | 'success' | 'warning' | 'danger' | 'neutral';

const props = withDefaults(
  defineProps<{
    tone?: Tone;
    title?: string;
    description?: string;
    compact?: boolean;
    closable?: boolean;
    closeLabel?: string;
  }>(),
  {
    tone: 'info',
    title: undefined,
    description: undefined,
    compact: false,
    closable: false,
    closeLabel: 'Закрыть уведомление'
  }
);

const emit = defineEmits<{
  close: [];
}>();

const role = computed(() => (props.tone === 'neutral' ? 'status' : 'alert'));

const close = () => {
  emit('close');
};
</script>

<template>
  <div
    class="ui-alert"
    :class="[`ui-alert--${tone}`, compact && 'ui-alert--compact']"
    :role="role"
  >
    <div
      class="ui-alert__icon"
      aria-hidden="true"
    >
      <slot name="icon">
        <span class="ui-alert__dot" />
      </slot>
    </div>
    <div class="ui-alert__content">
      <p
        v-if="title || $slots.title"
        class="ui-alert__title"
      >
        <slot name="title">
          {{ title }}
        </slot>
      </p>
      <div class="ui-alert__description">
        <slot>
          {{ description }}
        </slot>
      </div>
    </div>
    <UiIconButton
      v-if="closable"
      type="button"
      class="ui-alert__close"
      variant="ghost"
      size="sm"
      :aria-label="closeLabel"
      @click="close"
    >
      &times;
    </UiIconButton>
  </div>
</template>

<style scoped>
.ui-alert {
  --ui-alert-bg: var(--ui-card-bg-muted);
  --ui-alert-border: var(--ui-border);
  --ui-alert-highlight: color-mix(in srgb, var(--ui-alert-border) 36%, transparent);
  --ui-alert-icon-bg: color-mix(in srgb, var(--ui-alert-border) 18%, var(--ui-surface));
  --ui-alert-icon-color: var(--ui-foreground-muted);
  --ui-alert-text: var(--ui-foreground);

  position: relative;
  display: grid;
  grid-template-columns: auto minmax(0, 1fr) auto;
  gap: 0.75rem;
  align-items: start;
  overflow: hidden;
  padding: 1rem 1.0625rem;
  border: 1px solid var(--ui-alert-border);
  border-radius: var(--ui-radius-lg);
  background:
    linear-gradient(135deg, var(--ui-alert-highlight), transparent 42%),
    var(--ui-alert-bg);
  box-shadow: var(--ui-shadow-panel);
  color: var(--ui-alert-text);
}

.ui-alert::before {
  position: absolute;
  inset: 0 auto 0 0;
  width: 0.1875rem;
  background: var(--ui-alert-border);
  content: '';
}

.ui-alert__icon {
  display: inline-flex;
  width: 2.125rem;
  height: 2.125rem;
  align-items: center;
  justify-content: center;
  border: 1px solid color-mix(in srgb, var(--ui-alert-border) 46%, transparent);
  border-radius: var(--ui-radius-md);
  background: var(--ui-alert-icon-bg);
  box-shadow: inset 0 1px 0 color-mix(in srgb, var(--ui-foreground-inverse) 36%, transparent);
  color: var(--ui-alert-icon-color);
}

.ui-alert__dot {
  display: inline-block;
  width: 0.625rem;
  height: 0.625rem;
  border-radius: var(--ui-radius-full);
  background: currentColor;
  box-shadow: 0 0 0 0.25rem color-mix(in srgb, currentColor 14%, transparent);
}

.ui-alert__content {
  display: grid;
  min-width: 0;
  gap: 0.1875rem;
  padding-top: 0.0625rem;
}

.ui-alert__title {
  margin: 0;
  color: var(--ui-alert-text);
  font-size: 0.95rem;
  font-weight: 750;
  line-height: 1.35;
}

.ui-alert__description {
  color: var(--ui-foreground-muted);
  font-size: 0.9rem;
  line-height: 1.45;
}

.ui-alert__description :deep(p) {
  margin: 0.15rem 0 0;
}

.ui-alert__close {
  align-self: start;
  color: inherit;
}

.ui-alert--info {
  --ui-alert-bg: color-mix(in srgb, var(--ui-info) 10%, var(--ui-card-bg) 90%);
  --ui-alert-border: color-mix(in srgb, var(--ui-info) 54%, var(--ui-border) 46%);
  --ui-alert-highlight: color-mix(in srgb, var(--ui-info) 14%, transparent);
  --ui-alert-icon-bg: color-mix(in srgb, var(--ui-info) 16%, var(--ui-surface));
  --ui-alert-icon-color: var(--ui-status-info-text);
  --ui-alert-text: var(--ui-foreground);
}

.ui-alert--success {
  --ui-alert-bg: color-mix(in srgb, var(--ui-success) 10%, var(--ui-card-bg) 90%);
  --ui-alert-border: color-mix(in srgb, var(--ui-success) 56%, var(--ui-border) 44%);
  --ui-alert-highlight: color-mix(in srgb, var(--ui-success) 14%, transparent);
  --ui-alert-icon-bg: color-mix(in srgb, var(--ui-success) 16%, var(--ui-surface));
  --ui-alert-icon-color: var(--ui-status-success-text);
  --ui-alert-text: color-mix(in srgb, var(--ui-foreground) 85%, var(--ui-success) 15%);
}

.ui-alert--warning {
  --ui-alert-bg: color-mix(in srgb, var(--ui-warning) 11%, var(--ui-card-bg) 89%);
  --ui-alert-border: color-mix(in srgb, var(--ui-warning) 58%, var(--ui-border) 42%);
  --ui-alert-highlight: color-mix(in srgb, var(--ui-warning) 16%, transparent);
  --ui-alert-icon-bg: color-mix(in srgb, var(--ui-warning) 17%, var(--ui-surface));
  --ui-alert-icon-color: var(--ui-status-warning-text);
  --ui-alert-text: color-mix(in srgb, var(--ui-foreground) 84%, var(--ui-warning) 16%);
}

.ui-alert--danger {
  --ui-alert-bg: color-mix(in srgb, var(--ui-danger) 10%, var(--ui-card-bg) 90%);
  --ui-alert-border: color-mix(in srgb, var(--ui-danger) 60%, var(--ui-border) 40%);
  --ui-alert-highlight: color-mix(in srgb, var(--ui-danger) 16%, transparent);
  --ui-alert-icon-bg: color-mix(in srgb, var(--ui-danger) 16%, var(--ui-surface));
  --ui-alert-icon-color: var(--ui-status-danger-text);
  --ui-alert-text: color-mix(in srgb, var(--ui-foreground) 80%, var(--ui-danger) 20%);
}

.ui-alert--neutral {
  --ui-alert-bg: var(--ui-card-bg-muted);
  --ui-alert-border: var(--ui-border);
  --ui-alert-highlight: color-mix(in srgb, var(--ui-foreground-muted) 8%, transparent);
  --ui-alert-icon-bg: var(--ui-surface-muted);
  --ui-alert-icon-color: var(--ui-foreground-muted);
  --ui-alert-text: var(--ui-foreground);
}

.ui-alert--compact {
  gap: 0.625rem;
  padding: 0.6875rem 0.875rem;
  border-radius: var(--ui-radius-md);
}

.ui-alert--compact .ui-alert__icon {
  width: 1.875rem;
  height: 1.875rem;
}

@media (max-width: 640px) {
  .ui-alert {
    grid-template-columns: auto minmax(0, 1fr);
    padding: 0.875rem;
  }

  .ui-alert__close {
    grid-column: 2;
    justify-self: end;
  }
}
</style>
